// A greeting longer than either limit is cut off in the list on the memorial
// page and opened in full in a modal. Change these to change where it cuts.
export const GREETING_PREVIEW_MAX_LENGTH = 600;
export const GREETING_PREVIEW_MAX_LINES = 12;
// When all that would be hidden is the end of the last paragraph and it is
// about a line or two long, show the greeting in full instead.
export const GREETING_PREVIEW_MAX_TAIL = 100;

const ELLIPSIS = "…";
const WHITESPACE = /\s/;
const PARAGRAPH_BREAK = /\n\s*\n/;
const LAST_WORD = /\s\S*$/;
const TRAILING_PUNCTUATION = /[\s.,;:!?\-–—]+$/u;

/**
 * Turn the greeting as the API returns it (HTML-escaped plain text) into
 * plain text, so cutting it never splits an entity or a tag.
 *
 * @param {string} html
 * @returns {string}
 */
export function greetingToText(html = "") {
  if (!html) {
    return "";
  }

  const doc = new DOMParser().parseFromString(html, "text/html");

  return doc.body.textContent || "";
}

/**
 * Where to cut: the length limit, or the line break that would start one line
 * too many, whichever comes first.
 *
 * @param {string[]} chars
 * @param {number} maxLength
 * @param {number} maxLines
 * @returns {number}
 */
function findCut(chars, maxLength, maxLines) {
  const end = Math.min(chars.length, maxLength);
  let lines = 1;

  for (let i = 0; i < end; i += 1) {
    if (chars[i] === "\n") {
      lines += 1;
      if (lines > maxLines) {
        return i;
      }
    }
  }

  return end;
}

/**
 * Cut a greeting at a word boundary when it is longer than the limits. Line
 * breaks are kept, the cut never lands inside a surrogate pair, and trailing
 * whitespace and punctuation are dropped before the ellipsis.
 *
 * @param {string} text Plain text, see greetingToText().
 * @param {{ maxLength?: number, maxLines?: number, maxTail?: number }} [limits]
 * @returns {{ text: string, truncated: boolean }}
 */
export function truncateGreeting(
  text = "",
  {
    maxLength = GREETING_PREVIEW_MAX_LENGTH,
    maxLines = GREETING_PREVIEW_MAX_LINES,
    maxTail = GREETING_PREVIEW_MAX_TAIL,
  } = {}
) {
  const chars = Array.from(text.trim());
  const cut = findCut(chars, maxLength, maxLines);

  if (cut >= chars.length) {
    return { text, truncated: false };
  }

  const tail = chars.slice(cut);
  if (tail.length <= maxTail && !PARAGRAPH_BREAK.test(tail.join(""))) {
    return { text, truncated: false };
  }

  let preview = chars.slice(0, cut).join("");

  // Step back to the last whitespace unless the cut already sits on one, so
  // no word is split. A single word longer than the limit is cut as is.
  if (!WHITESPACE.test(chars[cut])) {
    const lastSpace = preview.search(LAST_WORD);
    if (lastSpace > 0) {
      preview = preview.slice(0, lastSpace);
    }
  }

  preview = preview.replace(TRAILING_PUNCTUATION, "");

  return { text: `${preview}${ELLIPSIS}`, truncated: true };
}
